import {useContext, useState} from "react";
import {Navigate} from "react-router-dom";
import {PageContext} from "../PageContext";
import {LoaderWithFetch} from "../components/Loader";
import {
    UsersAnalyticsData,
    IncomeAnalyticsData,
    RequestsAnalyticsData,
    RequestAnalysisData,
    ModelCostsAnalyticsData,
    ChurnAnalyticsData
} from "../dao/AnalyticsData";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import {Line, Bar} from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

const CHART_COLORS = {
    blue: "rgba(59, 130, 246, 1)",
    blueFaded: "rgba(59, 130, 246, 0.15)",
    green: "rgba(34, 197, 94, 1)",
    greenFaded: "rgba(34, 197, 94, 0.15)",
    purple: "rgba(168, 85, 247, 1)",
    purpleFaded: "rgba(168, 85, 247, 0.15)",
    red: "rgba(239, 68, 68, 1)",
    redFaded: "rgba(239, 68, 68, 0.15)",
    yellow: "rgba(234, 179, 8, 1)",
    yellowFaded: "rgba(234, 179, 8, 0.15)",
    gray: "rgba(156, 163, 175, 0.3)",
};

const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            labels: {color: "#e5e7eb"},
        },
    },
    scales: {
        x: {
            ticks: {color: "#9ca3af"},
            grid: {color: "rgba(75, 85, 99, 0.3)"},
        },
        y: {
            ticks: {color: "#9ca3af"},
            grid: {color: "rgba(75, 85, 99, 0.3)"},
            beginAtZero: true,
        },
    },
};

export default function AdminAnalyticsPage() {
    const context = useContext(PageContext);
    const [cacheKey, setCacheKey] = useState(0);

    if (context.loggedIn && !context.admin) {
        return <Navigate to="/"/>;
    }

    const clearCache = cacheKey > 0 ? "&clearCache=true" : "";

    return (
        <div className="overflow-hidden">
            <div className="section bg-neutral-800">
                <div className="container-sm" style={{maxWidth: "1200px"}}>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                        <h1>Analytics Dashboard</h1>
                        <button className="button-animated w-inline-block"
                                onClick={() => setCacheKey(k => k + 1)}>
                            <div className="button-label">Refresh</div>
                            <div className="button-hover-background"></div>
                        </button>
                    </div>
                    <div key={cacheKey} style={{display: "flex", flexDirection: "column", gap: "2rem", marginTop: "1.5rem"}}>
                        <UsersChart clearCache={clearCache}/>
                        <IncomeChart clearCache={clearCache}/>
                        <RequestsChart clearCache={clearCache}/>
                        <RequestAnalysisCharts clearCache={clearCache}/>
                        <ModelCostsTable clearCache={clearCache}/>
                        <ChurnChart clearCache={clearCache}/>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ChartCard(props: { title: string; children: React.ReactNode }) {
    return (
        <div style={{
            background: "rgba(30, 30, 40, 0.7)",
            borderRadius: "0.75rem",
            padding: "1.5rem",
            border: "1px solid rgba(75, 85, 99, 0.4)",
        }}>
            <h3 style={{marginBottom: "1rem", fontSize: "1.25rem"}}>{props.title}</h3>
            <div style={{height: "300px"}}>
                {props.children}
            </div>
        </div>
    );
}

function UsersChart(props: {clearCache: string}) {
    return (
        <LoaderWithFetch<UsersAnalyticsData> url={`/api/v1/analytics?type=users${props.clearCache}`}>
            {(data) => (
                <>
                    <ChartCard title="Total Users">
                        <Line
                            data={{
                                labels: data.months,
                                datasets: [
                                    {
                                        label: "Total Users",
                                        data: data.totalUsers,
                                        borderColor: CHART_COLORS.purple,
                                        backgroundColor: CHART_COLORS.purpleFaded,
                                        fill: true,
                                        tension: 0.3,
                                    },
                                ],
                            }}
                            options={commonOptions}
                        />
                    </ChartCard>
                    <ChartCard title="Signups & Active Users">
                        <Line
                            data={{
                                labels: data.months,
                                datasets: [
                                    {
                                        label: "Signups",
                                        data: data.signups,
                                        borderColor: CHART_COLORS.blue,
                                        backgroundColor: CHART_COLORS.blueFaded,
                                        fill: true,
                                        tension: 0.3,
                                    },
                                    {
                                        label: "Active Users",
                                        data: data.activeUsers,
                                        borderColor: CHART_COLORS.green,
                                        backgroundColor: CHART_COLORS.greenFaded,
                                        fill: true,
                                        tension: 0.3,
                                    },
                                ],
                            }}
                            options={commonOptions}
                        />
                    </ChartCard>
                </>
            )}
        </LoaderWithFetch>
    );
}

function IncomeChart(props: {clearCache: string}) {
    return (
        <LoaderWithFetch<IncomeAnalyticsData> url={`/api/v1/analytics?type=income${props.clearCache}`}>
            {(data) => (
                <ChartCard title="Income">
                    <Bar
                        data={{
                            labels: data.months,
                            datasets: [
                                {
                                    label: "Subscription",
                                    data: data.subscriptionIncome.map(c => c / 100),
                                    backgroundColor: CHART_COLORS.blue,
                                },
                                {
                                    label: "One-time",
                                    data: data.oneTimeIncome.map(c => c / 100),
                                    backgroundColor: CHART_COLORS.green,
                                },
                            ],
                        }}
                        options={{
                            ...commonOptions,
                            plugins: {
                                ...commonOptions.plugins,
                                tooltip: {
                                    callbacks: {
                                        label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toFixed(2)}`,
                                    },
                                },
                            },
                            scales: {
                                ...commonOptions.scales,
                                x: {...commonOptions.scales.x, stacked: true},
                                y: {
                                    ...commonOptions.scales.y,
                                    stacked: true,
                                    ticks: {
                                        ...commonOptions.scales.y.ticks,
                                        callback: (value) => `$${value}`,
                                    },
                                },
                            },
                        }}
                    />
                </ChartCard>
            )}
        </LoaderWithFetch>
    );
}

function RequestsChart(props: {clearCache: string}) {
    return (
        <LoaderWithFetch<RequestsAnalyticsData> url={`/api/v1/analytics?type=requests${props.clearCache}`}>
            {(data) => (
                <ChartCard title="Requests">
                    <Bar
                        data={{
                            labels: data.months,
                            datasets: [
                                {
                                    label: "Requests",
                                    data: data.requests,
                                    backgroundColor: CHART_COLORS.blue,
                                },
                            ],
                        }}
                        options={commonOptions}
                    />
                </ChartCard>
            )}
        </LoaderWithFetch>
    );
}

function RequestAnalysisCharts(props: {clearCache: string}) {
    return (
        <LoaderWithFetch<RequestAnalysisData> url={`/api/v1/analytics?type=requestAnalysis${props.clearCache}`}>
            {(data) => (
                <>
                    <ChartCard title="AI Costs vs Income">
                        <Line
                            data={{
                                labels: data.months,
                                datasets: [
                                    {
                                        label: "AI Cost",
                                        data: data.totalCost,
                                        borderColor: CHART_COLORS.red,
                                        backgroundColor: CHART_COLORS.redFaded,
                                        fill: true,
                                        tension: 0.3,
                                    },
                                    {
                                        label: "Income",
                                        data: data.monthlyIncome,
                                        borderColor: CHART_COLORS.green,
                                        backgroundColor: CHART_COLORS.greenFaded,
                                        fill: true,
                                        tension: 0.3,
                                    },
                                ],
                            }}
                            options={{
                                ...commonOptions,
                                plugins: {
                                    ...commonOptions.plugins,
                                    tooltip: {
                                        callbacks: {
                                            label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toFixed(2)}`,
                                        },
                                    },
                                },
                                scales: {
                                    ...commonOptions.scales,
                                    y: {
                                        ...commonOptions.scales.y,
                                        ticks: {
                                            ...commonOptions.scales.y.ticks,
                                            callback: (value) => `$${value}`,
                                        },
                                    },
                                },
                            }}
                        />
                    </ChartCard>
                    <ChartCard title="Unique Active Users">
                        <Bar
                            data={{
                                labels: data.months,
                                datasets: [
                                    {
                                        label: "Unique Users",
                                        data: data.uniqueUsers,
                                        backgroundColor: CHART_COLORS.purple,
                                    },
                                ],
                            }}
                            options={commonOptions}
                        />
                    </ChartCard>
                    <ChartCard title="BYOK vs Paid Requests">
                        <Bar
                            data={{
                                labels: data.months,
                                datasets: [
                                    {
                                        label: "BYOK",
                                        data: data.byokRequests,
                                        backgroundColor: CHART_COLORS.yellow,
                                    },
                                    {
                                        label: "Paid",
                                        data: data.paidRequests,
                                        backgroundColor: CHART_COLORS.green,
                                    },
                                ],
                            }}
                            options={{
                                ...commonOptions,
                                scales: {
                                    ...commonOptions.scales,
                                    x: {...commonOptions.scales.x, stacked: true},
                                    y: {...commonOptions.scales.y, stacked: true},
                                },
                            }}
                        />
                    </ChartCard>
                    <ChartCard title="Requests by IDE">
                        <Bar
                            data={{
                                labels: data.months,
                                datasets: [
                                    {
                                        label: "VS Code",
                                        data: data.vscodeRequests,
                                        backgroundColor: CHART_COLORS.blue,
                                    },
                                    {
                                        label: "JetBrains",
                                        data: data.jetbrainsRequests,
                                        backgroundColor: CHART_COLORS.purple,
                                    },
                                    {
                                        label: "Web",
                                        data: data.webRequests,
                                        backgroundColor: CHART_COLORS.green,
                                    },
                                ],
                            }}
                            options={{
                                ...commonOptions,
                                scales: {
                                    ...commonOptions.scales,
                                    x: {...commonOptions.scales.x, stacked: true},
                                    y: {...commonOptions.scales.y, stacked: true},
                                },
                            }}
                        />
                    </ChartCard>
                </>
            )}
        </LoaderWithFetch>
    );
}

function ModelCostsTable(props: {clearCache: string}) {
    const thStyle = {textAlign: "left" as const, padding: "0.5rem 0.75rem", color: "#9ca3af", fontSize: "0.8rem"};
    const thRight = {...thStyle, textAlign: "right" as const};
    const tdStyle = {padding: "0.5rem 0.75rem", color: "#e5e7eb"};
    const tdRight = {...tdStyle, textAlign: "right" as const};
    const creditsPerDollar = 60;

    return (
        <LoaderWithFetch<ModelCostsAnalyticsData> url={`/api/v1/analytics?type=modelCosts${props.clearCache}`}>
            {(data) => {
                const sorted = [...data.models].sort((a, b) => b.totalCost - a.totalCost);
                const isCostPlus = sorted.some((m) => m.realizedMargin !== undefined && m.realizedMargin !== null);
                if (isCostPlus) {
                    return (
                        <div style={{
                            background: "rgba(30, 30, 40, 0.7)",
                            borderRadius: "0.75rem",
                            padding: "1.5rem",
                            border: "1px solid rgba(75, 85, 99, 0.4)",
                        }}>
                            <h3 style={{marginBottom: "0.5rem", fontSize: "1.25rem"}}>Cost per Model (Last 30 Days)</h3>
                            <p style={{color: "#9ca3af", fontSize: "0.8rem", marginBottom: "1rem"}}>
                                Cost-plus pricing. Realized margin = charged / (cost × credits per $) − 1; red when below target.
                            </p>
                            <div style={{overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "collapse", fontSize: "0.85rem"}}>
                                    <thead>
                                    <tr style={{borderBottom: "1px solid rgba(75, 85, 99, 0.6)"}}>
                                        <th style={thStyle}>Model</th>
                                        <th style={thRight}>Requests</th>
                                        <th style={thRight}>Total Cost</th>
                                        <th style={thRight}>Avg Cost</th>
                                        <th style={thRight}>Charged cr</th>
                                        <th style={thRight}>Cost $</th>
                                        <th style={thRight}>Realized margin</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {sorted.map((m) => {
                                        const margin = m.realizedMargin;
                                        const hasMargin = margin !== undefined && margin !== null;
                                        const belowTarget = hasMargin && m.targetMargin !== undefined && m.targetMargin !== null
                                            && margin! < m.targetMargin;
                                        const marginColor = !hasMargin ? "#9ca3af" : (belowTarget ? "#ef4444" : "#22c55e");
                                        return (
                                            <tr key={m.type} style={{borderBottom: "1px solid rgba(75, 85, 99, 0.3)"}}>
                                                <td style={tdStyle}>{m.model}</td>
                                                <td style={tdRight}>{m.requests.toLocaleString()}</td>
                                                <td style={tdRight}>${m.totalCost.toFixed(2)}</td>
                                                <td style={tdRight}>${m.avgCost.toFixed(4)}</td>
                                                <td style={tdRight}>{m.chargedCredits != null ? m.chargedCredits.toLocaleString() : "—"}</td>
                                                <td style={tdRight}>{m.costUsd != null ? `$${m.costUsd.toFixed(2)}` : "—"}</td>
                                                <td style={{...tdRight, color: marginColor, fontWeight: 600}}>
                                                    {hasMargin ? `${(margin! * 100).toFixed(1)}%` : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                }
                let totalDollarDiff = 0;
                const rows = sorted.map((m) => {
                    const dollarDiff = (m.currentCredits - m.recommendedCredits) / creditsPerDollar * m.requests;
                    totalDollarDiff += dollarDiff;
                    const creditDiff = m.currentCredits - m.recommendedCredits;
                    const ratio = m.recommendedCredits > 0 ? m.currentCredits / m.recommendedCredits : 1;
                    let status: string;
                    let statusColor: string;
                    if (m.currentCredits === 0 && m.recommendedCredits === 0) {
                        status = "—";
                        statusColor = "#9ca3af";
                    } else if (ratio >= 0.95 && ratio <= 1.35) {
                        status = "OK";
                        statusColor = "#22c55e";
                    } else if (ratio > 1.35) {
                        status = "HIGH (+" + creditDiff + ")";
                        statusColor = "#eab308";
                    } else {
                        status = "LOW (" + creditDiff + ")";
                        statusColor = "#ef4444";
                    }
                    const diffColor = dollarDiff >= 0 ? "#22c55e" : "#ef4444";
                    const diffPrefix = dollarDiff >= 0 ? "+$" : "-$";
                    return (
                        <tr key={m.type} style={{borderBottom: "1px solid rgba(75, 85, 99, 0.3)"}}>
                            <td style={tdStyle}>{m.model}</td>
                            <td style={tdRight}>{m.requests.toLocaleString()}</td>
                            <td style={tdRight}>${m.totalCost.toFixed(2)}</td>
                            <td style={tdRight}>${m.avgCost.toFixed(4)}</td>
                            <td style={tdRight}>{m.currentCredits}</td>
                            <td style={tdRight}>{m.recommendedCredits}</td>
                            <td style={{...tdRight, color: diffColor}}>{diffPrefix}{Math.abs(dollarDiff).toFixed(2)}</td>
                            <td style={{...tdStyle, textAlign: "center", color: statusColor, fontWeight: 600}}>
                                {status}
                            </td>
                        </tr>
                    );
                });
                const totalColor = totalDollarDiff >= 0 ? "#22c55e" : "#ef4444";
                const totalPrefix = totalDollarDiff >= 0 ? "+$" : "-$";
                return (
                    <div style={{
                        background: "rgba(30, 30, 40, 0.7)",
                        borderRadius: "0.75rem",
                        padding: "1.5rem",
                        border: "1px solid rgba(75, 85, 99, 0.4)",
                    }}>
                        <h3 style={{marginBottom: "0.5rem", fontSize: "1.25rem"}}>Cost per Model (Last 30 Days)</h3>
                        <p style={{color: "#9ca3af", fontSize: "0.8rem", marginBottom: "1rem"}}>
                            Recommended credits assume 60 credits/$1 baseline with ~27.5% margin.
                        </p>
                        <div style={{overflowX: "auto"}}>
                            <table style={{width: "100%", borderCollapse: "collapse", fontSize: "0.85rem"}}>
                                <thead>
                                <tr style={{borderBottom: "1px solid rgba(75, 85, 99, 0.6)"}}>
                                    <th style={thStyle}>Model</th>
                                    <th style={thRight}>Requests</th>
                                    <th style={thRight}>Total Cost</th>
                                    <th style={thRight}>Avg Cost</th>
                                    <th style={thRight}>Current Cr.</th>
                                    <th style={thRight}>Rec. Cr.</th>
                                    <th style={thRight}>$ Diff</th>
                                    <th style={{...thStyle, textAlign: "center"}}>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rows}
                                <tr style={{borderTop: "2px solid rgba(75, 85, 99, 0.6)"}}>
                                    <td style={{...tdStyle, fontWeight: 700}} colSpan={6}>Total</td>
                                    <td style={{...tdRight, fontWeight: 700, color: totalColor}}>
                                        {totalPrefix}{Math.abs(totalDollarDiff).toFixed(2)}
                                    </td>
                                    <td/>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }}
        </LoaderWithFetch>
    );
}

function ChurnChart(props: {clearCache: string}) {
    return (
        <LoaderWithFetch<ChurnAnalyticsData> url={`/api/v1/analytics?type=churn${props.clearCache}`}>
            {(data) => (
                <ChartCard title="Subscriptions">
                    <Line
                        data={{
                            labels: data.months,
                            datasets: [
                                {
                                    label: "New",
                                    data: data.newSubscriptions,
                                    borderColor: CHART_COLORS.green,
                                    backgroundColor: CHART_COLORS.greenFaded,
                                    fill: true,
                                    tension: 0.3,
                                },
                                {
                                    label: "Canceled",
                                    data: data.canceledSubscriptions,
                                    borderColor: CHART_COLORS.red,
                                    backgroundColor: CHART_COLORS.redFaded,
                                    fill: true,
                                    tension: 0.3,
                                },
                                {
                                    label: "Net Change",
                                    data: data.netChange,
                                    borderColor: CHART_COLORS.yellow,
                                    backgroundColor: CHART_COLORS.yellowFaded,
                                    fill: false,
                                    tension: 0.3,
                                    borderDash: [5, 5],
                                },
                            ],
                        }}
                        options={commonOptions}
                    />
                </ChartCard>
            )}
        </LoaderWithFetch>
    );
}
